import { useState, useEffect, useCallback } from 'react'
import { GameState, SectRole } from '../store/types'
import {
  apiSectList, apiSectCreate, apiSectApply, apiSectInfo,
  apiSectApplyList, apiSectAccept, apiSectReject, apiSectLeave,
  apiSectStorageList, apiSectDeposit, apiSectWithdraw,
  apiSectVaultList, apiSectVaultDeposit, apiSectVaultWithdraw,
  apiSectFundDeposit, apiSectFundWithdraw, apiSectFundLog,
  apiAlchemyRecipeList, apiAlchemyCreate, apiCraftRecipeList, apiCraftCreate,
} from '../server/ServiceCall'

const GRADE_COLOR: Record<string, string> = {
  COMMON:'#a0a0a0', UNCOMMON:'#60c060', RARE:'#4090e0',
  EPIC:'#a060e0', LEGEND:'#e0a030', MYTH:'#e04060',
}

interface SectItem {
  sectId: number; sectName: string; leaderName: string
  memberCount: number; description: string | null; level: number; gold: number; hasApplied: boolean
}
interface Member {
  userCode: number; userName: string; sectRole: SectRole; totalPower: number; mugongStage: string
}
interface ApplyEntry {
  applyId: number; userCode: number; userName: string; totalPower: number; mugongStage: string
  message: string | null; applyDate: string
}
interface StorageItem {
  stoId: number; itemId: string; itemName: string; category: string
  grade: string; atkBonus: number; defBonus: number; hpBonus: number; speedBonus: number; qty: number
}

interface Props { gs: GameState; token: string; onRefresh: () => void }

type SubTab = 'info' | 'storage' | 'vault' | 'fund' | 'apply' | 'alchemy' | 'craft'

interface AlchemyRecipe {
  RECIPE_ID: string; RECIPE_NAME: string; RESULT_ITEM_ID: string
  BASE_SUCCESS_RATE: number; REQUIRED_ALCHEMY_LV: number; CRAFT_GOLD: number
  UNLOCKED: number; DESCRIPTION: string | null
}
interface AlchemyMaterial { RECIPE_ID: string; ITEM_ID: string; ITEM_NAME: string; QTY: number }
interface CraftRecipe {
  RECIPE_ID: string; RECIPE_NAME: string; RESULT_ITEM_ID: string
  BASE_SUCCESS_RATE: number; REQUIRED_CRAFT_LV: number; CRAFT_GOLD: number
  FAIL_RETURN_PCT: number; UNLOCKED: number; DESCRIPTION: string | null
}
interface CraftMaterial { RECIPE_ID: string; ITEM_ID: string; ITEM_NAME: string; QTY: number }
interface CraftSectInfo { ALCHEMY_LEVEL: number; ALCHEMY_EXP: number; CRAFT_LEVEL: number; CRAFT_EXP: number }

export default function TabSect({ gs, token, onRefresh }: Props) {
  const inSect   = gs.char.sectId != null
  const myRole   = gs.char.sectRole as SectRole | null
  const isLeader = myRole === '문주' || myRole === '장로'

  const [subTab,   setSubTab]   = useState<SubTab>('info')
  const [sects,    setSects]    = useState<SectItem[]>([])
  const [members,  setMembers]  = useState<Member[]>([])
  const [applies,  setApplies]  = useState<ApplyEntry[]>([])
  const [storage,  setStorage]  = useState<StorageItem[]>([])
  const [vault,    setVault]    = useState<StorageItem[]>([])
  const [fundLogs, setFundLogs] = useState<any[]>([])
  const [loading,  setLoading]  = useState(false)
  const [busy,     setBusy]     = useState(false)
  const [msg,      setMsg]      = useState('')
  const [msgOk,    setMsgOk]    = useState(false)

  // 연단 / 대장간
  const [alchemyData,  setAlchemyData]  = useState<{ sect: CraftSectInfo | null; recipes: AlchemyRecipe[]; materials: AlchemyMaterial[] }>({ sect: null, recipes: [], materials: [] })
  const [craftData,    setCraftData]    = useState<{ sect: CraftSectInfo | null; recipes: CraftRecipe[]; materials: CraftMaterial[] }>({ sect: null, recipes: [], materials: [] })
  const [selAlcRecipe, setSelAlcRecipe] = useState<string | null>(null)
  const [selCrfRecipe, setSelCrfRecipe] = useState<string | null>(null)
  const [useSectSto,   setUseSectSto]   = useState(false)

  // 창설 폼
  const [showCreate, setShowCreate] = useState(false)
  const [newName,    setNewName]    = useState('')
  const [newDesc,    setNewDesc]    = useState('')

  // 신청 선택
  const [selSect,    setSelSect]   = useState<SectItem | null>(null)
  const [applyMsg,   setApplyMsg]  = useState('')

  // 창고 선택
  const [selStoId,   setSelStoId]  = useState<number | null>(null)
  const [wdQty,      setWdQty]     = useState(1)
  const [depInvId,   setDepInvId]  = useState<number | null>(null)
  const [depQty,     setDepQty]    = useState(1)

  // 비고 선택
  const [selVaultId,  setSelVaultId]  = useState<number | null>(null)
  const [vaultWdQty,  setVaultWdQty]  = useState(1)
  const [vaultInvId,  setVaultInvId]  = useState<number | null>(null)
  const [vaultDepQty, setVaultDepQty] = useState(1)

  // 운영비
  const [fundAmount, setFundAmount] = useState(0)
  const [fundMemo,   setFundMemo]   = useState('')
  const [fundGold,   setFundGold]   = useState(0)

  function showMsg(m: string, ok = false) { setMsg(m); setMsgOk(ok); setTimeout(() => setMsg(''), 3000) }

  const loadSects = useCallback(() => {
    setLoading(true)
    apiSectList()
      .then(res => {
        if (res.resultCode === '00') {
          const raw = JSON.parse(res.resultData || '[]') as any[]
          setSects(raw.map(r => ({
            sectId:      Number(r.SECT_ID),
            sectName:    String(r.SECT_NAME),
            leaderName:  String(r.LEADER_NAME),
            memberCount: Number(r.MEMBER_COUNT) || 0,
            description: r.DESCRIPTION ? String(r.DESCRIPTION) : null,
            level:       Number(r.LEVEL) || 1,
            gold:        Number(r.GOLD)  || 0,
            hasApplied:  Number(r.HAS_APPLIED) === 1,
          })))
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const loadInfo = useCallback(() => {
    setLoading(true)
    apiSectInfo(token)
      .then(res => {
        if (res.resultCode === '00') {
          const raw = JSON.parse(res.resultData || '[]') as any[]
          setMembers(raw.map(r => ({
            userCode:    Number(r.USER_CODE),
            userName:    String(r.USER_NAME),
            sectRole:    String(r.SECT_ROLE) as SectRole,
            totalPower:  Number(r.TOTAL_POWER) || 0,
            mugongStage: String(r.MUGONG_STAGE),
          })))
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [token])

  const loadApplies = useCallback(() => {
    setLoading(true)
    apiSectApplyList(token)
      .then(res => {
        if (res.resultCode === '00') {
          const raw = JSON.parse(res.resultData || '[]') as any[]
          setApplies(raw.map(r => ({
            applyId:     Number(r.APPLY_ID),
            userCode:    Number(r.USER_CODE),
            userName:    String(r.USER_NAME),
            totalPower:  Number(r.TOTAL_POWER) || 0,
            mugongStage: String(r.MUGONG_STAGE),
            message:     r.MESSAGE ? String(r.MESSAGE) : null,
            applyDate:   String(r.APPLY_DATE ?? ''),
          })))
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [token])

  const loadStorage = useCallback(() => {
    setLoading(true)
    apiSectStorageList(token)
      .then(res => {
        if (res.resultCode === '00') {
          const raw = JSON.parse(res.resultData || '[]') as any[]
          setStorage(raw.map(r => ({
            stoId:      Number(r.STO_ID),
            itemId:     String(r.ITEM_ID),
            itemName:   String(r.ITEM_NAME),
            category:   String(r.CATEGORY),
            grade:      String(r.GRADE),
            atkBonus:   Number(r.ATK_BONUS)   || 0,
            defBonus:   Number(r.DEF_BONUS)   || 0,
            hpBonus:    Number(r.HP_BONUS)    || 0,
            speedBonus: Number(r.SPEED_BONUS) || 0,
            qty:        Number(r.QTY)         || 1,
          })))
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [token])

  const loadVault = useCallback(() => {
    setLoading(true)
    apiSectVaultList(token)
      .then(res => {
        if (res.resultCode === '00') {
          const raw = JSON.parse(res.resultData || '[]') as any[]
          setVault(raw.map(r => ({
            stoId:      Number(r.VAULT_ID),
            itemId:     String(r.ITEM_ID),
            itemName:   String(r.ITEM_NAME),
            category:   String(r.CATEGORY),
            grade:      String(r.GRADE),
            atkBonus:   Number(r.ATK_BONUS)   || 0,
            defBonus:   Number(r.DEF_BONUS)   || 0,
            hpBonus:    Number(r.HP_BONUS)    || 0,
            speedBonus: Number(r.SPEED_BONUS) || 0,
            qty:        Number(r.QTY)         || 1,
          })))
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [token])

  const loadFundLog = useCallback(() => {
    setLoading(true)
    apiSectFundLog(token)
      .then(res => {
        if (res.resultCode === '00') {
          const raw = JSON.parse(res.resultData || '[]') as any[]
          setFundLogs(raw)
          setFundGold(Number(res.resultMessage) || 0)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [token])

  const loadAlchemy = useCallback(() => {
    setLoading(true)
    apiAlchemyRecipeList(token)
      .then(res => {
        if (res.resultCode === '00') {
          const d = JSON.parse(res.resultData || '{}')
          setAlchemyData({
            sect:      d.sect?.[0] ?? null,
            recipes:   d.recipes  ?? [],
            materials: d.materials ?? [],
          })
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [token])

  const loadCraft = useCallback(() => {
    setLoading(true)
    apiCraftRecipeList(token)
      .then(res => {
        if (res.resultCode === '00') {
          const d = JSON.parse(res.resultData || '{}')
          setCraftData({
            sect:      d.sect?.[0] ?? null,
            recipes:   d.recipes  ?? [],
            materials: d.materials ?? [],
          })
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [token])

  useEffect(() => {
    if (!inSect) { loadSects(); return }
    if (subTab === 'info')    loadInfo()
    if (subTab === 'storage') loadStorage()
    if (subTab === 'vault')   loadVault()
    if (subTab === 'fund')    loadFundLog()
    if (subTab === 'apply')   loadApplies()
    if (subTab === 'alchemy') loadAlchemy()
    if (subTab === 'craft')   loadCraft()
  }, [inSect, subTab, loadSects, loadInfo, loadApplies, loadStorage, loadVault, loadFundLog, loadAlchemy, loadCraft])

  async function doCreate() {
    if (!newName.trim()) return
    setBusy(true)
    try {
      const res = await apiSectCreate(token, newName.trim(), newDesc.trim())
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setShowCreate(false); setNewName(''); setNewDesc(''); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doApply() {
    if (!selSect) return
    setBusy(true)
    try {
      const res = await apiSectApply(token, selSect.sectId, applyMsg)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setSelSect(null); setApplyMsg(''); loadSects() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doAccept(applyId: number) {
    setBusy(true)
    try {
      const res = await apiSectAccept(token, applyId)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { loadApplies(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doReject(applyId: number) {
    setBusy(true)
    try {
      const res = await apiSectReject(token, applyId)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') loadApplies()
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doLeave() {
    const confirmMsg = myRole === '문주' ? '문파를 해산합니다. 계속하시겠습니까?' : '문파를 탈퇴합니다. 계속하시겠습니까?'
    if (!window.confirm(confirmMsg)) return
    setBusy(true)
    try {
      const res = await apiSectLeave(token)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') onRefresh()
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doDeposit() {
    if (!depInvId) return
    setBusy(true)
    try {
      const res = await apiSectDeposit(token, depInvId, depQty)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setDepInvId(null); setDepQty(1); loadStorage(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doWithdraw() {
    if (!selStoId) return
    setBusy(true)
    try {
      const res = await apiSectWithdraw(token, selStoId, wdQty)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setSelStoId(null); setWdQty(1); loadStorage(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doVaultDeposit() {
    if (!vaultInvId) return
    setBusy(true)
    try {
      const res = await apiSectVaultDeposit(token, vaultInvId, vaultDepQty)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setVaultInvId(null); setVaultDepQty(1); loadVault(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doVaultWithdraw() {
    if (!selVaultId) return
    setBusy(true)
    try {
      const res = await apiSectVaultWithdraw(token, selVaultId, vaultWdQty)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setSelVaultId(null); setVaultWdQty(1); loadVault(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doFundDeposit() {
    if (fundAmount <= 0) return
    setBusy(true)
    try {
      const res = await apiSectFundDeposit(token, fundAmount, fundMemo)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setFundAmount(0); setFundMemo(''); loadFundLog(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doFundWithdraw() {
    if (fundAmount <= 0) return
    if (!window.confirm(`문파 금고에서 ${fundAmount.toLocaleString()}전을 출금합니다.`)) return
    setBusy(true)
    try {
      const res = await apiSectFundWithdraw(token, fundAmount, fundMemo)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') { setFundAmount(0); setFundMemo(''); loadFundLog(); onRefresh() }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doAlchemy() {
    if (!selAlcRecipe) return
    setBusy(true)
    try {
      const res = await apiAlchemyCreate(token, selAlcRecipe, useSectSto ? 1 : 0)
      if (res.resultCode === '00') {
        const d = JSON.parse(res.resultData || '{}')
        showMsg(d.SUCCESS_YN === 1
          ? `연단 성공! ${d.RESULT_ITEM ?? ''} ×${d.RESULT_QTY} 획득. 경험치 +${d.EXP_GAIN}`
          : `연단 실패. (성공률 ${d.USED_RATE}%)`, d.SUCCESS_YN === 1)
        loadAlchemy()
        onRefresh()
      } else {
        showMsg(res.resultMessage)
      }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doCraft() {
    if (!selCrfRecipe) return
    setBusy(true)
    try {
      const res = await apiCraftCreate(token, selCrfRecipe, useSectSto ? 1 : 0)
      if (res.resultCode === '00') {
        const d = JSON.parse(res.resultData || '{}')
        showMsg(d.SUCCESS_YN === 1
          ? `제조 성공! ${d.RESULT_ITEM ?? ''} 획득. 경험치 +${d.EXP_GAIN}`
          : `제조 실패. (성공률 ${d.USED_RATE}%)`, d.SUCCESS_YN === 1)
        loadCraft()
        onRefresh()
      } else {
        showMsg(res.resultMessage)
      }
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  const bagItems = gs.inventory.filter(i => !i.equipSlot)

  // ── 소속 없음 뷰 ──────────────────────────────────────────
  if (!inSect) {
    return (
      <div className="tab-action">
        {msg && <div className={`inven-msg${msgOk ? ' ok' : ''}`}>{msg}</div>}

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <div className="card-title" style={{ margin:0 }}>문파 목록</div>
          <button className="action-btn equip" style={{ padding:'4px 10px', fontSize:11 }}
            onClick={() => setShowCreate(!showCreate)}>문파 창설</button>
        </div>

        {showCreate && (
          <div style={{ background:'#1a1208', border:'1px solid #5a3a18', borderRadius:4, padding:10, marginBottom:8 }}>
            <div style={{ fontSize:12, color:'#c8b890', marginBottom:6 }}>
              문파 창설 비용: 3,000전 (보유: {gs.char.gold.toLocaleString()}전)
            </div>
            <input className="drop-qty-input" style={{ width:'100%', marginBottom:6 }}
              placeholder="문파명 (2~30자)" value={newName}
              onChange={e => setNewName(e.target.value)} maxLength={30} />
            <input className="drop-qty-input" style={{ width:'100%', marginBottom:6 }}
              placeholder="소개 (선택)" value={newDesc}
              onChange={e => setNewDesc(e.target.value)} maxLength={200} />
            <div style={{ display:'flex', gap:6 }}>
              <button className="action-btn equip" disabled={busy || !newName.trim()} onClick={doCreate}>창설</button>
              <button className="action-btn drop" onClick={() => setShowCreate(false)}>취소</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ color:'#605040', textAlign:'center', padding:'20px 0' }}>불러오는 중...</div>
        ) : sects.length === 0 ? (
          <div className="bag-empty">등록된 문파가 없습니다.</div>
        ) : (
          <div className="bag-list">
            {sects.map(s => (
              <div key={s.sectId}>
                <div className={`bag-item${selSect?.sectId === s.sectId ? ' selected' : ''}`}
                  onClick={() => setSelSect(selSect?.sectId === s.sectId ? null : s)}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span className="bag-item-name">{s.sectName}</span>
                      <span style={{ fontSize:10, color:'#a09060', border:'1px solid #5a4a20',
                        padding:'0 3px', borderRadius:2 }}>Lv.{s.level}</span>
                    </div>
                    <div style={{ fontSize:10, color:'#706050', marginTop:2 }}>
                      문주: {s.leaderName} · {s.memberCount}명
                    </div>
                  </div>
                  {s.hasApplied && (
                    <span style={{ fontSize:10, color:'#a0c080', border:'1px solid #406040',
                      padding:'1px 4px', borderRadius:2, flexShrink:0 }}>신청중</span>
                  )}
                </div>
                {selSect?.sectId === s.sectId && (
                  <div style={{ background:'#1a1208', border:'1px solid #5a3a18',
                    borderTop:'none', borderRadius:'0 0 4px 4px', padding:'8px 10px' }}>
                    {s.description && (
                      <div style={{ fontSize:11, color:'#a09060', marginBottom:6 }}>{s.description}</div>
                    )}
                    <input className="drop-qty-input" style={{ width:'100%', marginBottom:6 }}
                      placeholder="가입 인사 (선택)" value={applyMsg}
                      onChange={e => setApplyMsg(e.target.value)} maxLength={200} />
                    <button className="action-btn equip" disabled={busy || s.hasApplied}
                      onClick={doApply}>
                      {s.hasApplied ? '신청중' : '가입 신청'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  // ── 소속 있음 뷰 ──────────────────────────────────────────
  return (
    <div className="tab-action">
      {msg && <div className={`inven-msg${msgOk ? ' ok' : ''}`}>{msg}</div>}

      {/* 헤더 */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontWeight:'bold', fontSize:14, color:'#e0c060' }}>문파</span>
          <span style={{ fontSize:11, color:'#c8b890', border:'1px solid #5a4a20',
            padding:'1px 5px', borderRadius:2 }}>{myRole}</span>
        </div>
        <button className="action-btn drop" style={{ padding:'3px 8px', fontSize:11 }}
          disabled={busy} onClick={doLeave}>
          {myRole === '문주' ? '해산' : '탈퇴'}
        </button>
      </div>

      {/* 서브 탭 */}
      <div className="inven-view-tabs" style={{ marginBottom:8 }}>
        <button className={`inven-view-btn${subTab==='info'?' active':''}`}
          style={{ lineHeight:1.2 }}
          onClick={() => setSubTab('info')}>문파<br/>정보</button>
        <button className={`inven-view-btn${subTab==='storage'?' active':''}`}
          onClick={() => setSubTab('storage')}>창고</button>
        {isLeader && (
          <button className={`inven-view-btn${subTab==='vault'?' active':''}`}
            onClick={() => setSubTab('vault')}>비고</button>
        )}
        <button className={`inven-view-btn${subTab==='fund'?' active':''}`}
          onClick={() => setSubTab('fund')}>운영비</button>
        <button className={`inven-view-btn${subTab==='alchemy'?' active':''}`}
          onClick={() => setSubTab('alchemy')}>연단</button>
        <button className={`inven-view-btn${subTab==='craft'?' active':''}`}
          onClick={() => setSubTab('craft')}>대장간</button>
        {isLeader && (
          <button className={`inven-view-btn${subTab==='apply'?' active':''}`}
            style={{ lineHeight:1.2 }}
          onClick={() => setSubTab('apply')}>
            가입<br/>신청{applies.length > 0 ? ` (${applies.length})` : ''}
          </button>
        )}
      </div>

      {/* ── 정보 탭 ── */}
      {subTab === 'info' && (
        loading ? <div style={{ color:'#605040', textAlign:'center', padding:'20px 0' }}>불러오는 중...</div>
        : members.length === 0 ? <div className="bag-empty">정보 없음</div>
        : (
          <div className="bag-list">
            {members.map(m => (
              <div key={m.userCode} className="bag-item" style={{ cursor:'default' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <span className="bag-item-name">{m.userName}</span>
                    <span style={{ fontSize:10, color:
                      m.sectRole==='문주' ? '#e0c040' : m.sectRole==='장로' ? '#a090c0' : '#806050',
                      border:'1px solid #403020', padding:'0 3px', borderRadius:2 }}>
                      {m.sectRole}
                    </span>
                    <span style={{ fontSize:10, color:'#a0a0a0' }}>{m.mugongStage}</span>
                  </div>
                </div>
                <div style={{ color:'#e0c040', fontSize:12, fontWeight:'bold' }}>
                  {m.totalPower.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ── 창고 탭 ── */}
      {subTab === 'storage' && (
        <>
          {/* 입고 */}
          <div style={{ background:'#141008', border:'1px solid #3a2a18', borderRadius:4, padding:8, marginBottom:8 }}>
            <div style={{ fontSize:11, color:'#a09060', marginBottom:6 }}>창고에 넣기 (행낭 아이템)</div>
            {bagItems.length === 0 ? (
              <div style={{ fontSize:11, color:'#605040' }}>넣을 아이템이 없습니다.</div>
            ) : (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <select style={{ background:'#1a1208', border:'1px solid #5a3a18', color:'#c8b890',
                  fontSize:11, padding:'3px 6px', borderRadius:3 }}
                  value={depInvId ?? ''}
                  onChange={e => { setDepInvId(e.target.value ? Number(e.target.value) : null); setDepQty(1) }}>
                  <option value=''>아이템 선택</option>
                  {bagItems.map(i => (
                    <option key={i.invId} value={i.invId}>{i.name}{i.qty > 1 ? ` ×${i.qty}` : ''}</option>
                  ))}
                </select>
                {depInvId && (() => {
                  const inv = bagItems.find(i => i.invId === depInvId)
                  return inv && inv.qty > 1 ? (
                    <input type="number" className="drop-qty-input" style={{ width:50 }}
                      min={1} max={inv.qty} value={depQty}
                      onChange={e => setDepQty(Math.max(1, Math.min(inv.qty, Number(e.target.value))))} />
                  ) : null
                })()}
                <button className="action-btn equip" style={{ padding:'3px 10px', fontSize:11 }}
                  disabled={busy || !depInvId} onClick={doDeposit}>입고</button>
              </div>
            )}
          </div>

          {/* 창고 목록 */}
          {loading ? (
            <div style={{ color:'#605040', textAlign:'center', padding:'20px 0' }}>불러오는 중...</div>
          ) : storage.length === 0 ? (
            <div className="bag-empty">창고가 비어 있습니다.</div>
          ) : (
            <div className="bag-list">
              {storage.map(s => (
                <div key={s.stoId}
                  className={`bag-item${selStoId===s.stoId?' selected':''}`}
                  onClick={() => { setSelStoId(selStoId===s.stoId ? null : s.stoId); setWdQty(1) }}>
                  <span className="bag-grade-dot" style={{ background: GRADE_COLOR[s.grade] }} />
                  <span className="bag-item-name">{s.itemName}</span>
                  {s.qty > 1 && <span className="bag-item-qty">×{s.qty}</span>}
                </div>
              ))}
            </div>
          )}

          {selStoId && (() => {
            const s = storage.find(x => x.stoId === selStoId)
            if (!s) return null
            return (
              <div className="item-action-panel">
                <div className="item-action-title" style={{ color: GRADE_COLOR[s.grade] }}>{s.itemName}</div>
                <div className="item-action-btns">
                  {s.qty > 1 && (
                    <input type="number" className="drop-qty-input" min={1} max={s.qty} value={wdQty}
                      onChange={e => setWdQty(Math.max(1, Math.min(s.qty, Number(e.target.value))))} />
                  )}
                  <button className="action-btn equip" disabled={busy} onClick={doWithdraw}>출고</button>
                </div>
              </div>
            )
          })()}
        </>
      )}

      {/* ── 비고 탭 (문주/장로) ── */}
      {subTab === 'vault' && isLeader && (
        <>
          {/* 입고 */}
          <div style={{ background:'#14100a', border:'1px solid #5a3a28', borderRadius:4, padding:8, marginBottom:8 }}>
            <div style={{ fontSize:11, color:'#c09060', marginBottom:6 }}>비고에 넣기 (행낭 아이템 · 문주/장로 전용)</div>
            {bagItems.length === 0 ? (
              <div style={{ fontSize:11, color:'#605040' }}>넣을 아이템이 없습니다.</div>
            ) : (
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                <select style={{ background:'#1a1208', border:'1px solid #6a4028', color:'#c8b890',
                  fontSize:11, padding:'3px 6px', borderRadius:3 }}
                  value={vaultInvId ?? ''}
                  onChange={e => { setVaultInvId(e.target.value ? Number(e.target.value) : null); setVaultDepQty(1) }}>
                  <option value=''>아이템 선택</option>
                  {bagItems.map(i => (
                    <option key={i.invId} value={i.invId}>{i.name}{i.qty > 1 ? ` ×${i.qty}` : ''}</option>
                  ))}
                </select>
                {vaultInvId && (() => {
                  const inv = bagItems.find(i => i.invId === vaultInvId)
                  return inv && inv.qty > 1 ? (
                    <input type="number" className="drop-qty-input" style={{ width:50 }}
                      min={1} max={inv.qty} value={vaultDepQty}
                      onChange={e => setVaultDepQty(Math.max(1, Math.min(inv.qty, Number(e.target.value))))} />
                  ) : null
                })()}
                <button className="action-btn equip" style={{ padding:'3px 10px', fontSize:11 }}
                  disabled={busy || !vaultInvId} onClick={doVaultDeposit}>입고</button>
              </div>
            )}
          </div>

          {/* 비고 목록 */}
          {loading ? (
            <div style={{ color:'#605040', textAlign:'center', padding:'20px 0' }}>불러오는 중...</div>
          ) : vault.length === 0 ? (
            <div className="bag-empty">비고가 비어 있습니다.</div>
          ) : (
            <div className="bag-list">
              {vault.map(s => (
                <div key={s.stoId}
                  className={`bag-item${selVaultId===s.stoId?' selected':''}`}
                  onClick={() => { setSelVaultId(selVaultId===s.stoId ? null : s.stoId); setVaultWdQty(1) }}>
                  <span className="bag-grade-dot" style={{ background: GRADE_COLOR[s.grade] }} />
                  <span className="bag-item-name">{s.itemName}</span>
                  {s.qty > 1 && <span className="bag-item-qty">×{s.qty}</span>}
                </div>
              ))}
            </div>
          )}

          {selVaultId && (() => {
            const s = vault.find(x => x.stoId === selVaultId)
            if (!s) return null
            return (
              <div className="item-action-panel">
                <div className="item-action-title" style={{ color: GRADE_COLOR[s.grade] }}>{s.itemName}</div>
                <div className="item-action-btns">
                  {s.qty > 1 && (
                    <input type="number" className="drop-qty-input" min={1} max={s.qty} value={vaultWdQty}
                      onChange={e => setVaultWdQty(Math.max(1, Math.min(s.qty, Number(e.target.value))))} />
                  )}
                  <button className="action-btn equip" disabled={busy} onClick={doVaultWithdraw}>출고</button>
                </div>
              </div>
            )
          })()}
        </>
      )}

      {/* ── 운영비 탭 ── */}
      {subTab === 'fund' && (
        <>
          {/* 금고 현황 */}
          <div style={{ background:'#14100a', border:'1px solid #5a4a20', borderRadius:4, padding:'8px 12px', marginBottom:8 }}>
            <div style={{ fontSize:11, color:'#a09060', marginBottom:2 }}>문파 금고</div>
            <div style={{ fontSize:18, fontWeight:'bold', color:'#e0c040' }}>
              {fundGold.toLocaleString()}전
            </div>
          </div>

          {/* 입출금 */}
          <div style={{ background:'#141008', border:'1px solid #3a2a18', borderRadius:4, padding:8, marginBottom:8 }}>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
              <input type="number" className="drop-qty-input" style={{ width:100 }}
                min={1} placeholder="금액(전)"
                value={fundAmount || ''}
                onChange={e => setFundAmount(Math.max(0, Number(e.target.value)))} />
              <input className="drop-qty-input" style={{ flex:1, minWidth:80 }}
                placeholder="메모(선택)" maxLength={50}
                value={fundMemo}
                onChange={e => setFundMemo(e.target.value)} />
              <button className="action-btn equip" style={{ padding:'3px 10px', fontSize:11 }}
                disabled={busy || fundAmount <= 0} onClick={doFundDeposit}>입금</button>
              {isLeader && (
                <button className="action-btn drop" style={{ padding:'3px 10px', fontSize:11 }}
                  disabled={busy || fundAmount <= 0} onClick={doFundWithdraw}>출금</button>
              )}
            </div>
            {!isLeader && (
              <div style={{ fontSize:10, color:'#604838', marginTop:4 }}>출금은 문주/장로만 가능합니다.</div>
            )}
          </div>

          {/* 로그 */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4 }}>
            <div style={{ fontSize:11, color:'#a09060' }}>입출금 내역 (최근 50건)</div>
            <button className="action-btn equip" style={{ padding:'2px 8px', fontSize:10 }}
              onClick={loadFundLog}>새로고침</button>
          </div>
          {loading ? (
            <div style={{ color:'#605040', textAlign:'center', padding:'12px 0' }}>불러오는 중...</div>
          ) : fundLogs.length === 0 ? (
            <div className="bag-empty">입출금 내역이 없습니다.</div>
          ) : (
            <div className="bag-list">
              {fundLogs.map((log: any) => (
                <div key={log.LOG_ID} className="bag-item" style={{ cursor:'default' }}>
                  <span style={{ fontSize:11, color: log.TYPE === 'IN' ? '#60c080' : '#e06050',
                    minWidth:16, textAlign:'center', flexShrink:0 }}>
                    {log.TYPE === 'IN' ? '↑' : '↓'}
                  </span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <span style={{ fontSize:12, color: log.TYPE === 'IN' ? '#60c080' : '#e06050', fontWeight:'bold' }}>
                        {log.TYPE === 'IN' ? '+' : '-'}{Number(log.AMOUNT).toLocaleString()}전
                      </span>
                      <span style={{ fontSize:10, color:'#c8b890' }}>{log.USER_NAME}</span>
                      {log.MEMO && <span style={{ fontSize:10, color:'#806050' }}>"{log.MEMO}"</span>}
                    </div>
                    <div style={{ fontSize:10, color:'#605040' }}>
                      잔액 {Number(log.BALANCE).toLocaleString()}전 · {log.LOG_DATE}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── 연단 탭 ── */}
      {subTab === 'alchemy' && (
        loading ? <div style={{ color:'#605040', textAlign:'center', padding:'20px 0' }}>불러오는 중...</div>
        : (
          <div>
            {alchemyData.sect && (
              <div style={{ background:'#141008', border:'1px solid #3a2a18', borderRadius:4, padding:8, marginBottom:8, fontSize:11 }}>
                <span style={{ color:'#a09060' }}>연단 레벨: </span>
                <span style={{ color:'#e0c040', fontWeight:'bold' }}>{alchemyData.sect.ALCHEMY_LEVEL}Lv</span>
                <span style={{ color:'#606060', margin:'0 8px' }}>|</span>
                <span style={{ color:'#a09060' }}>EXP: </span>
                <span style={{ color:'#c0a060' }}>{Number(alchemyData.sect.ALCHEMY_EXP).toLocaleString()}</span>
              </div>
            )}
            <div style={{ marginBottom:6, display:'flex', alignItems:'center', gap:8 }}>
              <label style={{ fontSize:11, color:'#a09060', display:'flex', alignItems:'center', gap:4 }}>
                <input type="checkbox" checked={useSectSto} onChange={e => setUseSectSto(e.target.checked)} />
                문파 창고 재료 사용
              </label>
            </div>
            <div className="bag-list">
              {alchemyData.recipes.map(r => {
                const mats = alchemyData.materials.filter(m => m.RECIPE_ID === r.RECIPE_ID)
                const isLocked = !r.UNLOCKED
                return (
                  <div key={r.RECIPE_ID}
                    className={`bag-item${selAlcRecipe===r.RECIPE_ID?' selected':''}${isLocked?' disabled':''}`}
                    style={{ cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.5 : 1 }}
                    onClick={() => !isLocked && setSelAlcRecipe(selAlcRecipe===r.RECIPE_ID ? null : r.RECIPE_ID)}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span className="bag-item-name">{r.RECIPE_NAME}</span>
                        <span style={{ fontSize:10, color:'#8080a0' }}>Lv{r.REQUIRED_ALCHEMY_LV}~</span>
                        <span style={{ fontSize:10, color:'#60c080' }}>{r.BASE_SUCCESS_RATE}%</span>
                      </div>
                      <div style={{ fontSize:10, color:'#705040', marginTop:2 }}>
                        {mats.map(m => `${m.ITEM_NAME}×${m.QTY}`).join(' + ')}
                      </div>
                      {r.DESCRIPTION && (
                        <div style={{ fontSize:10, color:'#504838', marginTop:1 }}>{r.DESCRIPTION}</div>
                      )}
                    </div>
                    <div style={{ color:'#e0c040', fontSize:11 }}>{r.CRAFT_GOLD.toLocaleString()}냥</div>
                  </div>
                )
              })}
            </div>
            {selAlcRecipe && (
              <button className="action-btn equip" style={{ width:'100%', marginTop:8 }}
                disabled={busy} onClick={doAlchemy}>
                {busy ? '연단 중...' : '연단 시작'}
              </button>
            )}
          </div>
        )
      )}

      {/* ── 대장간 탭 ── */}
      {subTab === 'craft' && (
        loading ? <div style={{ color:'#605040', textAlign:'center', padding:'20px 0' }}>불러오는 중...</div>
        : (
          <div>
            {craftData.sect && (
              <div style={{ background:'#141008', border:'1px solid #3a2a18', borderRadius:4, padding:8, marginBottom:8, fontSize:11 }}>
                <span style={{ color:'#a09060' }}>대장간 레벨: </span>
                <span style={{ color:'#e0c040', fontWeight:'bold' }}>{craftData.sect.CRAFT_LEVEL}Lv</span>
                <span style={{ color:'#606060', margin:'0 8px' }}>|</span>
                <span style={{ color:'#a09060' }}>EXP: </span>
                <span style={{ color:'#c0a060' }}>{Number(craftData.sect.CRAFT_EXP).toLocaleString()}</span>
              </div>
            )}
            <div style={{ marginBottom:6, display:'flex', alignItems:'center', gap:8 }}>
              <label style={{ fontSize:11, color:'#a09060', display:'flex', alignItems:'center', gap:4 }}>
                <input type="checkbox" checked={useSectSto} onChange={e => setUseSectSto(e.target.checked)} />
                문파 창고 재료 사용
              </label>
            </div>
            <div className="bag-list">
              {craftData.recipes.map(r => {
                const mats = craftData.materials.filter(m => m.RECIPE_ID === r.RECIPE_ID)
                const isLocked = !r.UNLOCKED
                return (
                  <div key={r.RECIPE_ID}
                    className={`bag-item${selCrfRecipe===r.RECIPE_ID?' selected':''}${isLocked?' disabled':''}`}
                    style={{ cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.5 : 1 }}
                    onClick={() => !isLocked && setSelCrfRecipe(selCrfRecipe===r.RECIPE_ID ? null : r.RECIPE_ID)}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span className="bag-item-name">{r.RECIPE_NAME}</span>
                        <span style={{ fontSize:10, color:'#8080a0' }}>Lv{r.REQUIRED_CRAFT_LV}~</span>
                        <span style={{ fontSize:10, color:'#60c080' }}>{r.BASE_SUCCESS_RATE}%</span>
                        <span style={{ fontSize:10, color:'#a06040' }}>실패반환{r.FAIL_RETURN_PCT}%</span>
                      </div>
                      <div style={{ fontSize:10, color:'#705040', marginTop:2 }}>
                        {mats.map(m => `${m.ITEM_NAME}×${m.QTY}`).join(' + ')}
                      </div>
                      {r.DESCRIPTION && (
                        <div style={{ fontSize:10, color:'#504838', marginTop:1 }}>{r.DESCRIPTION}</div>
                      )}
                    </div>
                    <div style={{ color:'#e0c040', fontSize:11 }}>{r.CRAFT_GOLD.toLocaleString()}냥</div>
                  </div>
                )
              })}
            </div>
            {selCrfRecipe && (
              <button className="action-btn equip" style={{ width:'100%', marginTop:8 }}
                disabled={busy} onClick={doCraft}>
                {busy ? '제조 중...' : '제조 시작'}
              </button>
            )}
          </div>
        )
      )}

      {/* ── 신청자 탭 ── */}
      {subTab === 'apply' && isLeader && (
        loading ? <div style={{ color:'#605040', textAlign:'center', padding:'20px 0' }}>불러오는 중...</div>
        : applies.length === 0 ? <div className="bag-empty">가입 신청이 없습니다.</div>
        : (
          <div className="bag-list">
            {applies.map(a => (
              <div key={a.applyId} style={{
                background:'#1a1208', border:'1px solid #3a2a18',
                borderRadius:4, padding:'8px 10px', marginBottom:4,
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontWeight:'bold', color:'#c8b890' }}>{a.userName}</span>
                  <span style={{ fontSize:11, color:'#a0a0a0' }}>{a.mugongStage}</span>
                  <span style={{ fontSize:11, color:'#e0c040', marginLeft:'auto' }}>
                    전투력 {a.totalPower.toLocaleString()}
                  </span>
                </div>
                {a.message && (
                  <div style={{ fontSize:11, color:'#907060', marginBottom:6 }}>"{a.message}"</div>
                )}
                <div style={{ display:'flex', gap:6 }}>
                  <button className="action-btn equip" style={{ padding:'3px 10px', fontSize:11 }}
                    disabled={busy} onClick={() => doAccept(a.applyId)}>승인</button>
                  <button className="action-btn drop" style={{ padding:'3px 10px', fontSize:11 }}
                    disabled={busy} onClick={() => doReject(a.applyId)}>거절</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}
